import type { Subscription } from 'rxjs';

/**
 * One in-flight request, and the three facts about it that must never disagree.
 *
 * A component that loads something cancellable ends up tracking three things: a
 * generation counter (is this response still wanted?), the subscription (stop
 * the work), and a pending flag (what the template waits on). They describe one
 * request, so they have to move as one — and when they are three loose fields,
 * they don't.
 *
 * That is not a tidiness argument. The Supporting pages evaluation kept those
 * three as separate fields and desynced twice, the same way both times: a
 * caller invalidated the request without releasing the flag, the abandoned
 * response arrived, the `is this still current?` guard returned early — before
 * clearing anything — and the flag stayed true with nothing running. The
 * review surface reads that flag to decide whether the page has settled, so it
 * waited forever, on that page and on every page after it. Two callers, two
 * bugs, one shape.
 *
 * So the state is private and the only way to abandon a request is `cancel()`,
 * which moves all three. The staleness guard is still needed and still right —
 * a response from an abandoned request must not clear a flag that a NEWER
 * request set. It is safe only because cancelling clears the flag itself.
 */
export class InFlightRequest {
  private generation = 0;
  private subscription: Subscription | null = null;
  private running = false;

  /** What the UI waits on: a request is actually in flight right now. */
  get pending(): boolean {
    return this.running;
  }

  /**
   * Abandon whatever is running and take a token for the request replacing it.
   * Pass the token back to `settle` so a late response can identify itself.
   */
  begin(): number {
    this.cancel();
    this.running = true;
    return this.generation;
  }

  /** Hand over the subscription so a later `cancel` can stop the work. */
  track(subscription: Subscription): void {
    this.subscription = subscription;
  }

  /**
   * Record that the request holding `token` has finished.
   *
   * Returns false when the response belongs to an abandoned request, which is
   * the caller's signal to discard the payload — its data describes a question
   * nobody is asking any more.
   */
  settle(token: number): boolean {
    if (token !== this.generation) return false;
    this.running = false;
    this.subscription = null;
    return true;
  }

  /** Stop the work, invalidate the response, and stop waiting. Together. */
  cancel(): void {
    this.generation++;
    this.subscription?.unsubscribe();
    this.subscription = null;
    this.running = false;
  }
}
